import { SymbolType, Project, TradeSymbol, SystemType } from '@shared/types/tsk';
import { SYSTEM_COLORS } from '../utils/drawing';
import { getSymbolAcronym } from '../utils/symbolUtils';
import { TradeLegend } from './TradeLegend';


interface ReportExportTemplateProps {
  project: Project | undefined;
  fixtureCounts: Record<string, number>;
  fittingCounts: Record<string, number>;
  aiStatement: string;
  flattenedPlan: string | null;
}

/**
 * A dedicated, pixel-perfect template for manual slicing PDF generation.
 * (T-TradeSketch-017)
 * Uses fixed 794x1123 bounds (A4 @ 96 DPI).
 */
export const ReportExportTemplate = ({
  project,
  fixtureCounts,
  fittingCounts,
  aiStatement,
  flattenedPlan
}: ReportExportTemplateProps) => {
  return (
    <div id="report-export-root" className="bg-white text-slate-900 font-sans" style={{ width: '794px', backgroundColor: 'white' }}>
      {/* PAGE 1 */}
      <div 
        className="page-break"
        style={{ 
          width: '794px', 
          height: '1123px', 
          padding: '16mm', // Mandated 16mm padding
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          position: 'relative',
          boxSizing: 'border-box',
          overflow: 'hidden' // Hurdle 17.1
        }}
      >
        {/* Header Block with Branding (Hurdle 18.1) */}
        <div style={{ textAlign: 'center', borderBottom: '2px solid #ff914d', paddingBottom: '6mm', marginBottom: '6mm' }}>
          <img src="/brand/logo-full-dark.png" style={{ height: '12mm', marginBottom: '4mm' }} alt="CadOps Logo" />
          <h1 style={{ fontSize: '20pt', fontWeight: 900, textTransform: 'uppercase', margin: 0, color: '#0f172a' }}>
            As Constructed Report
          </h1>
          <p style={{ fontSize: '11pt', fontWeight: 700, color: '#475569', textTransform: 'uppercase', margin: '1mm 0' }}>
            Water and Sanitary Drainage
          </p>
          <div style={{ fontSize: '8pt', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
            Ref: TS-{project?.id.slice(-5)} • {new Date().toLocaleDateString('en-AU')}
          </div>
        </div>
        {/* Prominent Project Address Header (Hurdle T-TSK-017A) */}
        {project?.address && (
          <div
            data-testid="report-project-address"
            style={{
              backgroundColor: '#0f172a',
              color: '#ff914d',
              borderRadius: '3mm',
              padding: '4mm 6mm',
              marginBottom: '4mm',
              fontSize: '13pt',
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              lineHeight: 1.2,
              wordBreak: 'break-word'
            }}
          >
            {project.address}
          </div>
        )}
        {/* Metadata Grid - Tightened for zero overflow (Hurdle 17.1) */}
        <div style={{ 
          backgroundColor: '#f8fafc', 
          border: '0.5pt solid #0f172a', 
          padding: '5mm', 
          borderRadius: '3mm',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '6mm 10mm',
          marginBottom: '6mm'
        }}>

          <MetadataItem label="Property Address" value={project?.address} full />
          <MetadataItem label="Lot" value={project?.lot} />
          <MetadataItem label="RP / Plan No." value={project?.rp} />
          <MetadataItem label="Site Classification" value={project?.siteClassification} />
          <MetadataItem label="Approval No." value={project?.approvalNo} />
        </div>

        {/* Tables Section */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10mm' }}>
          <div>
            <h2 style={{ fontSize: '8pt', fontWeight: 900, textTransform: 'uppercase', color: '#64748b', marginBottom: '2mm' }}>Fixture Schedule</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '0.5pt solid #0f172a' }}>
              <thead style={{ backgroundColor: '#f8fafc', color: '#0f172a', borderBottom: '0.5pt solid #0f172a' }}>
                <tr>
                  <th style={{ padding: '2mm', fontSize: '7pt', textAlign: 'left', textTransform: 'uppercase', fontWeight: 900 }}>Fixture</th>
                  <th style={{ padding: '2mm', fontSize: '7pt', textAlign: 'right', textTransform: 'uppercase', fontWeight: 900 }}>Qty</th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(fixtureCounts).map(([name, count]) => (
                  <tr key={name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '2mm', fontSize: '9pt', fontWeight: 700 }}>{name}</td>
                    <td style={{ padding: '2mm', fontSize: '9pt', fontWeight: 900, textAlign: 'right' }}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h2 style={{ fontSize: '8pt', fontWeight: 900, textTransform: 'uppercase', color: '#64748b', marginBottom: '2mm' }}>Fittings & Details</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '0.5pt solid #0f172a' }}>
              <thead style={{ backgroundColor: '#f8fafc', color: '#0f172a', borderBottom: '0.5pt solid #0f172a' }}>
                <tr>
                  <th style={{ padding: '2mm', fontSize: '7pt', textAlign: 'left', textTransform: 'uppercase', fontWeight: 900 }}>Item</th>
                  <th style={{ padding: '2mm', fontSize: '7pt', textAlign: 'right', textTransform: 'uppercase', fontWeight: 900 }}>Qty</th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(fittingCounts).map(([name, count]) => (
                  <tr key={name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '2mm', fontSize: '9pt', fontWeight: 700 }}>{name}</td>
                    <td style={{ padding: '2mm', fontSize: '9pt', fontWeight: 900, textAlign: 'right' }}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* AI Statement */}
        <div style={{ marginTop: 'auto', paddingTop: '6mm', borderTop: '1px solid #f1f5f9', fontStyle: 'italic', color: '#475569', fontSize: '9pt', lineHeight: 1.4 }}>
          {aiStatement}
        </div>

        {/* Signature Area */}
        <div style={{ marginTop: '6mm', paddingTop: '6mm', borderTop: '3px solid #ff914d', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
             <div style={{ width: '60mm', height: '15mm', border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '7pt', marginBottom: '2mm', backgroundColor: '#fafafa' }}>
               <span style={{ margin: 'auto' }}>Signature Area</span>
             </div>
             <div style={{ width: '60mm', borderBottom: '1px solid #0f172a' }} />
             <p style={{ fontSize: '7pt', fontWeight: 900, textTransform: 'uppercase', color: '#ff914d', marginTop: '1mm' }}>Verification Required</p>
          </div>
          <div style={{ textAlign: 'right' }}>
             <div style={{ display: 'flex', alignItems: 'center', gap: '2mm', justifyContent: 'flex-end', marginBottom: '2mm' }}>
                <span style={{ fontSize: '7pt', fontWeight: 900, color: '#64748b' }}>Powered by TradeSketch</span>
                <img src="/brand/logo-symbol.png" style={{ height: '5mm' }} alt="TS Logo" />
             </div>
             <p style={{ fontSize: '6pt', color: '#94a3b8', margin: 0 }}>A professional product of CadOps Pty Ltd</p>
             <p style={{ fontSize: '6pt', color: '#cbd5e1', marginTop: '1mm' }}>Page 1 of 2</p>
          </div>
        </div>

      </div>

      {/* PAGE 2 */}
      <div 
        className="page-break"
        style={{ 
          width: '794px', 
          height: '1123px', 
          padding: '16mm',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          position: 'relative',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #ff914d', paddingBottom: '4mm', marginBottom: '6mm' }}>

          <div>
            <h2 style={{ fontSize: '16pt', fontWeight: 900, textTransform: 'uppercase', margin: 0 }}>Site Plan</h2>
            <p style={{ fontSize: '8pt', color: '#94a3b8', margin: '1mm 0' }}>{project?.address}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '10pt', fontWeight: 900 }}>REF: TS-{project?.id.slice(-5)}</div>
            <div style={{ fontSize: '7pt', color: '#94a3b8' }}>Page 2 of 2</div>
          </div>
        </div>

        {/* Plan Area */}
        <div
          data-testid="report-plan-area"
          style={{ flex: 1, border: '0.5pt solid #0f172a', position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', minHeight: 0, overflow: 'hidden' }}
        >
          {flattenedPlan && (
            <img
              data-testid="report-plan-image"
              src={flattenedPlan}
              style={{ width: '100%', height: 'auto', display: 'block' }}
              alt="Site Plan"
            />
          )}
                   {/* Unified Symbol Legend (Hurdle 21.2) */}
          <TradeLegend 
            markup={project?.markup || { lines: [], symbols: [] }} 
            variant="export" 
            className="absolute bottom-4 right-4" 
          />
        </div>

        <div style={{ marginTop: '6mm', paddingTop: '4mm', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>

           <div style={{ fontSize: '7pt', fontWeight: 900, color: '#0f172a' }}>TradeSketch Professional Output</div>
           <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2mm', justifyContent: 'flex-end', marginBottom: '1mm' }}>
                 <span style={{ fontSize: '7pt', fontWeight: 900, color: '#64748b' }}>Powered by TradeSketch</span>
                 <img src="/brand/logo-symbol.png" style={{ height: '4mm' }} alt="TS Logo" />
              </div>
              <p style={{ fontSize: '6pt', color: '#94a3b8', margin: 0 }}>A professional product of CadOps Pty Ltd</p>
              <p style={{ fontSize: '6pt', color: '#cbd5e1', marginTop: '1mm' }}>Page 2 of 2</p>
           </div>
        </div>

      </div>
    </div>
  );
};

const MetadataItem = ({ label, value, full }: { label: string; value: string | undefined; full?: boolean }) => (
  <div style={{ gridColumn: full ? 'span 2' : 'span 1' }}>
    <label style={{ fontSize: '7pt', fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: '1mm' }}>{label}</label>
    <div style={{ fontSize: '10pt', fontWeight: 900, color: '#0f172a', borderBottom: '2px solid #f1f5f9', paddingBottom: '1mm', minHeight: '5mm', lineHeight: 1.2 }}>
      {value || 'DATA NOT ENTERED'}
    </div>
  </div>
);
;
